import React, { useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Box, Typography, LinearProgress, Alert, Card, CardContent,
  Chip, Tooltip, Grid, IconButton, Divider, Button, Menu, MenuItem
} from '@mui/material';
import {
  CalendarToday as CalendarIcon, CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon, Pending as PendingIcon, MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon, Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs from 'dayjs';
import {
  formatJobTime,
  formatJobEnteredAt,
  formatJobScheduledFor,
  jobMatchesDay,
  jobsToCalendarEvents,
  parseJobScheduledStart,
} from '../utils/jobSchedule';
import {
  fetchAllCollectionsFromApi,
  getCachedJobsList,
  setCachedJobsList,
} from '../utils/jobsListCache';

export default function Calendar() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const calendarRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDay, setSelectedDay] = useState(() => dayjs());
  const [cardMenuAnchor, setCardMenuAnchor] = useState(null);
  const [cardMenuJob, setCardMenuJob] = useState(null);

  const calendarHeaderToolbar = useMemo(
    () =>
      isNarrowScreen
        ? { left: 'prev,next', center: 'title', right: 'today' }
        : { left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' },
    [isNarrowScreen]
  );

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    const v = api.view.type;
    if (isNarrowScreen) {
      if (v === 'timeGridWeek') api.changeView('timeGridDay');
    } else if (v === 'timeGridDay') {
      api.changeView('timeGridWeek');
    }
  }, [isNarrowScreen]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const cached = getCachedJobsList();
        const list = cached?.length ? cached : await fetchAllCollectionsFromApi();
        if (cancelled) return;
        setCachedJobsList(list);
        setJobs(list);
        setEvents(jobsToCalendarEvents(list));
        setError('');
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Failed to load jobs');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedDayJobs = useMemo(() => {
    const day = selectedDay;
    const list = Array.isArray(jobs) ? jobs : [];
    const filtered = list.filter((j) => jobMatchesDay(j, day));
    filtered.sort((a, b) => {
      const da = parseJobScheduledStart(a);
      const db = parseJobScheduledStart(b);
      if (da && db) return da.diff(db);
      if (da && !db) return -1;
      if (!da && db) return 1;
      // fallback: newest first
      const ca = a.created_at ? new Date(a.created_at).getTime() : 0;
      const cb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return cb - ca;
    });
    return filtered;
  }, [jobs, selectedDay]);

  function renderEventContent(eventInfo) {
    const { event } = eventInfo;
    const props = event.extendedProps;
    
    const getStatusColor = (status, isProblem) => {
      if (isProblem) return '#f59e0b';
      switch (status) {
        case 'completed': return '#10b981';
        case 'in_progress': return '#3b82f6';
        case 'pending': return '#f59e0b';
        case 'cancelled': return '#ef4444';
        default: return '#64748b';
      }
    };

    const getStatusIcon = (status) => {
      switch (status) {
        case 'completed': return <CheckCircleIcon sx={{ fontSize: 16 }} />;
        case 'in_progress': return <ScheduleIcon sx={{ fontSize: 16 }} />;
        case 'pending': return <PendingIcon sx={{ fontSize: 16 }} />;
        default: return <ScheduleIcon sx={{ fontSize: 16 }} />;
      }
    };

    const tooltip = (
      <Box sx={{ minWidth: 280, p: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Job Details
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box><strong>Client:</strong> {event.title.split(':').pop()}</Box>
          {event.title.includes(':') && (
            <Box><strong>Driver:</strong> {event.title.split(':')[0]}</Box>
          )}
          {props.address && <Box><strong>Address:</strong> {props.address}</Box>}
          {props.phone && <Box><strong>Phone:</strong> {props.phone}</Box>}
          {props.email && <Box><strong>Email:</strong> {props.email}</Box>}
          {props.waste_type && <Box><strong>Waste Type:</strong> {props.waste_type}</Box>}
          {props.notes && <Box><strong>Notes:</strong> {props.notes}</Box>}
          {props.price && <Box><strong>Price:</strong> {props.price} {props.currency}</Box>}
          {props.status && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <strong>Status:</strong>
              <Chip
                icon={getStatusIcon(props.status)}
                label={props.status.replace('_', ' ')}
                size="small"
                sx={{
                  backgroundColor: `${getStatusColor(props.status, props.is_problem)}20`,
                  color: getStatusColor(props.status, props.is_problem),
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  height: 20
                }}
              />
            </Box>
          )}
          <Box><strong>Entered in system:</strong> {formatJobEnteredAt({ created_at: props.created_at }) || 'Not set'}</Box>
          <Box><strong>Scheduled for work:</strong> {formatJobScheduledFor(props) || 'Not set'}</Box>
          {props.driver_notes && <Box><strong>Driver Notes:</strong> {props.driver_notes}</Box>}
          {props.files && props.files.length > 0 && (
            <Box><strong>Files:</strong> {props.files.length} attached</Box>
          )}
        </Box>
      </Box>
    );

    return (
      <Tooltip title={tooltip} arrow placement="top" enterDelay={200}>
        <Box sx={{ 
          cursor: 'pointer', 
          p: 0.5, 
          borderRadius: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          border: `2px solid ${getStatusColor(props.status, props.is_problem)}`,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
            transform: 'scale(1.02)',
          },
          transition: 'all 0.2s ease-in-out'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem', mb: 0.5 }}>
            {event.title}
          </Typography>
          {props.scheduled_time && (
            <Typography variant="caption" sx={{ 
              color: '#3b82f6', 
              fontWeight: 600,
              display: 'block',
              mb: 0.5
            }}>
              {props.scheduled_time}
            </Typography>
          )}
          {props.address && (
            <Typography variant="caption" sx={{ 
              fontSize: '0.7rem',
              display: 'block',
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {props.address}
            </Typography>
          )}
          {props.waste_type && (
            <Typography variant="caption" sx={{ 
              fontSize: '0.7rem',
              color: '#64748b',
              display: 'block'
            }}>
              {props.waste_type}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  }

  function getEventClassNames(arg) {
    const status = arg.event.extendedProps.status;
    if (status && status.toLowerCase() === 'completed') {
      return ['job-completed'];
    }
    return ['job-active'];
  }

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <LinearProgress sx={{ width: '100%', maxWidth: 400 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 700, 
            color: '#1e293b', 
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1.2,
          }}
        >
          Calendar View
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#64748b',
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1.4,
          }}
        >
          View and manage all scheduled jobs in a calendar format.
        </Typography>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Calendar Card */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              width: 48, height: 48, borderRadius: '12px', 
              background: 'linear-gradient(135deg, #f59e0b20 0%, #f59e0b10 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#f59e0b'
            }}>
              <CalendarIcon />
            </Box>
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: 1.2,
                }}
              >
                Job Calendar
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: 1.4,
                }}
              >
                {events.length} jobs on calendar · {jobs.length} total loaded
              </Typography>
            </Box>
          </Box>

          <Box sx={{ 
            '& .fc': { 
              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' 
            },
            '& .fc-header-toolbar': {
              flexWrap: 'wrap',
              rowGap: 8,
              columnGap: 8,
              justifyContent: 'center',
            },
            '& .fc-toolbar-chunk': {
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
            },
            '& .fc-toolbar': {
              backgroundColor: '#f8fafc',
              borderRadius: 2,
              p: 2,
              mb: 2
            },
            '& .fc-toolbar-title': {
              fontWeight: 600,
              color: '#1e293b',
              fontSize: { xs: '0.9rem', sm: '1.05rem', md: '1.25rem' },
              lineHeight: 1.25,
              whiteSpace: 'normal',
              textAlign: 'center',
              maxWidth: '100%',
              px: { xs: 0.5, sm: 1 },
            },
            '& .fc-button': {
              backgroundColor: '#2563eb',
              borderColor: '#2563eb',
              fontWeight: 600,
              fontSize: { xs: '0.7rem', sm: '0.8125rem' },
              padding: { xs: '4px 8px', sm: '6px 12px' },
              '&:hover': {
                backgroundColor: '#1d4ed8',
                borderColor: '#1d4ed8'
              }
            },
            '& .fc-button-active': {
              backgroundColor: '#1d4ed8',
              borderColor: '#1d4ed8'
            },
            '& .fc-daygrid-day': {
              backgroundColor: '#ffffff'
            },
            '& .fc-daygrid-day:hover': {
              backgroundColor: '#f8fafc'
            },
            '& .fc-day-today': {
              backgroundColor: 'rgba(37, 99, 235, 0.05)'
            },
            '& .fc-col-header-cell': {
              backgroundColor: '#f1f5f9',
              fontWeight: 600,
              color: '#64748b'
            },
            '& .fc-timegrid-slot': {
              backgroundColor: '#ffffff'
            },
            '& .fc-timegrid-slot:hover': {
              backgroundColor: '#f8fafc'
            },
            '& .job-completed': {
              backgroundColor: '#dcfce7 !important',
              borderColor: '#10b981 !important',
              color: '#166534 !important'
            },
            '& .job-active': {
              backgroundColor: '#dbeafe !important',
              borderColor: '#3b82f6 !important',
              color: '#1e40af !important'
            }
          }}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              headerToolbar={calendarHeaderToolbar}
              events={events}
              eventContent={renderEventContent}
              eventClassNames={getEventClassNames}
              dateClick={(info) => {
                setSelectedDay(dayjs(info.date));
              }}
              height="auto"
              dayMaxEvents={true}
              moreLinkClick="popover"
              eventClick={(info) => {
                console.log('Event clicked:', info.event);
              }}
              selectable={true}
              selectMirror={true}
              weekends={true}
              firstDay={1}
              slotMinTime="06:00:00"
              slotMaxTime="22:00:00"
              allDaySlot={true}
              slotDuration="00:30:00"
              slotLabelInterval="01:00"
              expandRows={true}
              stickyHeaderDates={true}
              nowIndicator={true}
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5, 6],
                startTime: '08:00',
                endTime: '18:00',
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Calendar Events */}
      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Jobs for {selectedDay.format('dddd, D MMMM YYYY')}
              </Typography>
              <Chip
                size="small"
                label={`${selectedDayJobs.length} job${selectedDayJobs.length === 1 ? '' : 's'}`}
                sx={{ fontWeight: 700 }}
              />
              <Box sx={{ flex: 1 }} />
              <Button size="small" variant="outlined" onClick={() => setSelectedDay(dayjs())}>
                Today
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {selectedDayJobs.length === 0 ? (
              <Typography color="text.secondary">No jobs for this day.</Typography>
            ) : (
              <Grid container spacing={2}>
                {selectedDayJobs.map((job) => {
                  const status = job.status || 'scheduled';
                  const statusColor =
                    status === 'completed'
                      ? 'success'
                      : status === 'cancelled'
                        ? 'error'
                        : status === 'in_progress'
                          ? 'info'
                          : 'warning';
                  return (
                    <Grid item xs={12} md={6} lg={4} key={job.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          borderRadius: 2,
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#2563eb',
                                flex: '0 0 auto',
                              }}
                            >
                              <CalendarIcon fontSize="small" />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                                {formatJobTime(job) ? `${formatJobTime(job)} · ` : ''}
                                {job.driver ? `${job.driver}: ` : ''}
                                {job.client || job.name || 'Job'}
                              </Typography>
                              {job.address ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {job.address}
                                </Typography>
                              ) : null}
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                Entered: {formatJobEnteredAt(job) || '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Scheduled: {formatJobScheduledFor(job) || '—'}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.75 }}>
                            <Chip
                              size="small"
                              label={String(status).replace('_', ' ')}
                              color={statusColor}
                              sx={{ textTransform: 'capitalize', fontWeight: 700 }}
                            />
                            {Array.isArray(job.files) && job.files.length > 0 ? (
                              <Chip size="small" label={`${job.files.length} file${job.files.length === 1 ? '' : 's'}`} />
                            ) : null}
                            <Box sx={{ flex: 1 }} />
                            <IconButton
                              size="small"
                              aria-label="Job actions"
                              onClick={(e) => {
                                setCardMenuAnchor(e.currentTarget);
                                setCardMenuJob(job);
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Box>

      <Menu
        anchorEl={cardMenuAnchor}
        open={Boolean(cardMenuAnchor)}
        onClose={() => {
          setCardMenuAnchor(null);
          setCardMenuJob(null);
        }}
        PaperProps={{
          elevation: 8,
          sx: { borderRadius: '12px', minWidth: 160, mt: 1 },
        }}
      >
        <MenuItem
          onClick={() => {
            if (cardMenuJob?.id) {
              navigate('/jobs', { state: { action: 'view', jobId: cardMenuJob.id } });
            }
            setCardMenuAnchor(null);
            setCardMenuJob(null);
          }}
          sx={{ py: 1.5, mx: 1, borderRadius: '8px' }}
        >
          <VisibilityIcon sx={{ mr: 1.5, fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            View details
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (cardMenuJob?.id) {
              navigate('/jobs', { state: { action: 'edit', jobId: cardMenuJob.id } });
            }
            setCardMenuAnchor(null);
            setCardMenuJob(null);
          }}
          sx={{ py: 1.5, mx: 1, borderRadius: '8px' }}
        >
          <EditIcon sx={{ mr: 1.5, fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Edit job
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
} 